/**
 * ProtectedRoute.tsx
 * ──────────────────
 * Guards app routes behind the unified session.
 *
 * isAuthenticated / isLoading / roles / permissions all come from the
 * SESSION STORE via session hooks — NOT from Auth0 directly.
 *
 * Auth0 loginWithRedirect() is still called for unauthenticated users.
 */

import React, { ReactNode, useEffect, useReducer, useState } from "react";
import { useLocation } from "wouter";
import { useAuth0 } from "@auth0/auth0-react";
import { canAccessFeature } from "@/lib/role-checks";
import { Forbidden, ForbiddenPermission, ForbiddenPlanLimit } from "./Forbidden";
import { AuthenticatedLayout } from "./AuthenticatedLayout";
import {
  getSession,
  hasExplicitLogoutHandoff,
  onSessionChange,
} from "@/lib/sessionStore";

export interface ProtectedRouteProps {
  allowedRoles?: string[];
  requiredFeature?: string;
  requiredPermission?: string | string[];
  children: ReactNode;
}

function pickPrimaryRole(roles: string[]): string | undefined {
  if (!roles?.length) return undefined;
  const priority = ["Admin", "Plus", "Premium", "Tester", "Lite"];
  for (const p of priority) if (roles.includes(p)) return p;
  return roles[0];
}

function hasAnyPermission(userPerms: string[], required?: string | string[]) {
  if (!required) return true;
  const needed = Array.isArray(required) ? required : [required];
  return needed.some((p) => userPerms.includes(p));
}

/** Reads auth state from session store, re-renders on change. */
function useSessionAuth() {
  const [, forceUpdate] = useReducer((x: number) => x + 1, 0);
  useEffect(() => onSessionChange(forceUpdate), []);
  const session = getSession();
  return {
    isLoading: !session.initialized,
    isAuthenticated: session.initialized && session.mode !== "none",
    roles: session.user?.roles,
    permissions: session.user?.permissions,
    primaryRole: pickPrimaryRole(session.user?.roles ?? []),
  };
}

export function ProtectedRoute({
  allowedRoles,
  requiredFeature,
  requiredPermission,
  children,
}: ProtectedRouteProps) {
  const { isLoading, isAuthenticated, roles, permissions, primaryRole } = useSessionAuth();
  const { loginWithRedirect } = useAuth0();
  const [location] = useLocation();
  const [kicked, setKicked] = useState(false);

  // Redirect unauthenticated users to Auth0 login
  useEffect(() => {
    if (!isLoading && !isAuthenticated && !kicked) {
      if (hasExplicitLogoutHandoff()) return;
      setKicked(true);
      loginWithRedirect({ appState: { returnTo: location || "/hubs" } });
    }
  }, [isLoading, isAuthenticated, kicked, loginWithRedirect, location]);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-muted-foreground">
        Loading…
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center text-muted-foreground">
        Redirecting to login…
      </div>
    );
  }

  // Roles/permissions arrive as part of bootstrap — wait if not yet resolved
  if (!roles || !permissions) {
    return (
      <div className="min-h-screen flex items-center justify-center text-muted-foreground">
        Loading roles…
      </div>
    );
  }

  if (allowedRoles?.length && !roles.some((r) => allowedRoles.includes(r))) {
    console.log("[ProtectedRoute] Access denied. Required:", allowedRoles, "User has:", roles);
    return (
      <Forbidden
        message="Access Denied"
        description={`Your account role (${roles.join(", ")}) doesn't have permission. Required: ${allowedRoles.join(", ")}`}
      />
    );
  }

  if (!hasAnyPermission(permissions, requiredPermission)) {
    console.log("[ProtectedRoute] Permission denied. Required:", requiredPermission, "User has:", permissions);
    return <ForbiddenPermission />;
  }

  if (requiredFeature) {
    const roleForFeature = primaryRole || "";
    if (!canAccessFeature(roleForFeature, requiredFeature)) {
      return <ForbiddenPlanLimit />;
    }
  }

  return <AuthenticatedLayout>{children}</AuthenticatedLayout>;
}

export default ProtectedRoute;
